import {
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Tag,
  Tooltip,
  message,
} from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  createTemplateLabel,
  deleteTemplateLabel,
  getTemplateLabels,
  updateTemplateLabel,
} from "../../api/settings/email-template";

const COLOR_OPTIONS = [
  { label: "Default", value: "default" },
  { label: "Success", value: "success" },
  { label: "Processing", value: "processing" },
  { label: "Warning", value: "warning" },
  { label: "Error", value: "error" },
  { label: "Blue", value: "blue" },
  { label: "Green", value: "green" },
  { label: "Gold", value: "gold" },
  { label: "Volcano", value: "volcano" },
  { label: "Purple", value: "purple" },
];

const TemplateLabelDrawer = ({ open, onClose, onLabelsChange }) => {
  const [allLabels, setAllLabels] = useState([]);
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ name: "", color: "default" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      getTemplateLabels()
        .then(setAllLabels)
        .catch(() => message.error("Failed to load labels"));
    }
  }, [open]);

  const handleStartEdit = (label) => {
    setEditingId(label.id);
    setEditValues({ name: label.name, color: label.color });
  };

  const handleSaveEdit = async () => {
    if (!editValues.name.trim()) {
      message.warning("Name is required");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateTemplateLabel(editingId, {
        name: editValues.name.toUpperCase(),
        color: editValues.color,
      });
      setAllLabels((prev) => prev.map((l) => (l.id === editingId ? updated : l)));
      onLabelsChange((prev) => prev.map((l) => (l.id === editingId ? updated : l)));
      setEditingId(null);
    } catch (err) {
      message.error(err?.message || "Failed to update label");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (labelId) => {
    try {
      await deleteTemplateLabel(labelId);
      setAllLabels((prev) => prev.filter((l) => l.id !== labelId));
      onLabelsChange((prev) => prev.filter((l) => l.id !== labelId));
    } catch (err) {
      message.error(err?.message || "Failed to delete label");
    }
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      const newLabel = await createTemplateLabel({
        ...values,
        name: values.name.toUpperCase(),
      });
      setAllLabels((prev) => [...prev, newLabel]);
      onLabelsChange((prev) => [...prev, newLabel]);
      form.resetFields();
      message.success("Label created");
    } catch (err) {
      message.error(err?.message || "Failed to create label");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Drawer
      open={open}
      mask={false}
      title="Manage Labels"
      onClose={onClose}
      width={340}
    >
      <div style={{ marginBottom: 4 }}>
        {allLabels.length === 0 && (
          <div style={{ color: "rgba(0,0,0,0.35)", fontSize: 13, padding: "8px 0" }}>
            No labels yet — create one below
          </div>
        )}
        {allLabels.map((label, i) => {
          const isEditing = editingId === label.id;
          const borderBottom = i < allLabels.length - 1 ? "1px solid #f0f0f0" : "none";

          if (isEditing) {
            return (
              <div
                key={label.id}
                style={{
                  padding: "10px 8px",
                  borderBottom,
                  background: "#fafafa",
                  borderRadius: 6,
                  marginBottom: 2,
                }}
              >
                <Input
                  value={editValues.name}
                  onChange={(e) =>
                    setEditValues((v) => ({ ...v, name: e.target.value }))
                  }
                  style={{ textTransform: "uppercase", marginBottom: 8 }}
                  autoFocus
                  onPressEnter={handleSaveEdit}
                  placeholder="Label name"
                />
                <Select
                  value={editValues.color}
                  onChange={(v) => setEditValues((ev) => ({ ...ev, color: v }))}
                  style={{ width: "100%", marginBottom: 10 }}
                  options={COLOR_OPTIONS.map((c) => ({
                    value: c.value,
                    label: <Tag color={c.value}>{c.label}</Tag>,
                  }))}
                />
                <Row gutter={8}>
                  <Col flex="1">
                    <Button block type="primary" size="small" loading={saving} onClick={handleSaveEdit}>
                      Save
                    </Button>
                  </Col>
                  <Col flex="1">
                    <Button block size="small" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </div>
            );
          }

          return (
            <div
              key={label.id}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px 4px",
                userSelect: "none",
                borderBottom,
              }}
            >
              <Tag
                color={label.color}
                style={{ margin: 0, marginRight: 8, borderRadius: 5, padding: "4px 8px", lineHeight: 1 }}
              >
                {label.name}
              </Tag>
              <div style={{ flex: 1 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Tooltip title="Edit">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined style={{ fontSize: 11 }} />}
                    onClick={() => handleStartEdit(label)}
                    style={{ opacity: 0.45, padding: "0 4px" }}
                  />
                </Tooltip>
                <Popconfirm
                  title="Delete this label from all templates?"
                  onConfirm={() => handleDelete(label.id)}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                >
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                      style={{ opacity: 0.4, padding: "0 4px" }}
                    />
                  </Tooltip>
                </Popconfirm>
              </div>
            </div>
          );
        })}
      </div>

      <Divider style={{ fontSize: 13 }}>Create New Label</Divider>

      <Form form={form} layout="vertical" onFinish={handleCreate}>
        <Row gutter={8}>
          <Col flex="1">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Enter a name" }]}
            >
              <Input placeholder="E.G. BACKEND" style={{ textTransform: "uppercase" }} />
            </Form.Item>
          </Col>
          <Col flex="120px">
            <Form.Item name="color" label="Color" initialValue="default">
              <Select
                options={COLOR_OPTIONS.map((c) => ({
                  value: c.value,
                  label: <Tag color={c.value}>{c.label}</Tag>,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit" loading={creating} block>
          Create Label
        </Button>
      </Form>
    </Drawer>
  );
};

TemplateLabelDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLabelsChange: PropTypes.func.isRequired,
};

export default TemplateLabelDrawer;
